import React, { useState } from 'react';
import { Heart, MessageCircle, Bookmark, Plus, Play, Tag, MoreHorizontal, MapPin, Building2, Umbrella, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Avatar, Verified, LocationDot, RichText, useNow } from './shared';
import { stories, homePosts, taggableUsers, shopProducts, ytThumb } from '../mock';
import YouTubeModal from './YouTubeModal';
import CommentsSheet from './CommentsSheet';
import ActionSheet from './ActionSheet';
import ProductSheet from './ProductSheet';

const notify = (msg) => toast.success(msg, { style: { background: '#0f1622', color: '#fff', border: '1px solid #1c2634' } });

function DiscoverChips() {
  const navigate = useNavigate();
  const chips = [
    { l: 'Nearby', Icon: MapPin, to: '/places' },
    { l: 'Hotels', Icon: Building2, to: '/places' },
    { l: 'Beaches', Icon: Umbrella, to: '/places' },
    { l: 'Shop', Icon: ShoppingBag, to: '/shop' },
  ];
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 pb-3">
      {chips.map((c) => (
        <button key={c.l} onClick={() => navigate(c.to)} className="shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#141c28] text-[13px] font-medium text-[#c4ccd8] active:scale-95 transition-transform">
          <c.Icon size={14} className="text-[#2f8bff]" /> {c.l}
        </button>
      ))}
    </div>
  );
}

function StoriesRow() {
  return (
    <div className="flex gap-4 overflow-x-auto no-scrollbar px-4 py-4">
      {stories.map((s) => (
        <button key={s.id} className="flex flex-col items-center gap-1.5 shrink-0 active:scale-95 transition-transform">
          <div className="relative">
            <Avatar src={s.avatar} size={62} ring={!s.seen} />
            {s.seen && <span className="absolute inset-0 rounded-full ring-2 ring-[#1f2a3a]" />}
            {s.isSelf && (
              <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#2f8bff] flex items-center justify-center ring-2 ring-[#05070c]">
                <Plus size={13} className="text-white" strokeWidth={3} />
              </span>
            )}
          </div>
          <span className="text-[11px] text-[#c4ccd8] max-w-[64px] truncate">{s.name}</span>
        </button>
      ))}
    </div>
  );
}

function PostCard({ post }) {
  const [liked, setLiked] = useState(post.liked);
  const [pop, setPop] = useState(false);
  const [saved, setSaved] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comments);
  const likeCount = post.likes + (liked && !post.liked ? 1 : 0) - (!liked && post.liked ? 1 : 0);

  const toggleLike = () => { setLiked((v) => !v); setPop(true); setTimeout(() => setPop(false), 360); };
  const toggleSave = () => { setSaved((v) => { const nv = !v; notify(nv ? 'Saved to your collection' : 'Removed from saved'); return nv; }); };
  const tagged = (post.tagged || []).map((h) => taggableUsers.find((u) => u.handle === h)).filter(Boolean);
  const product = post.productId ? shopProducts.find((p) => p.id === post.productId) : null;

  return (
    <article className="animate-fade-up">
      <div className="flex items-center gap-3 px-4 py-3">
        <Avatar src={post.avatar} size={40} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold text-white truncate">{post.user}</span>
            {post.verified && <Verified size={14} />}
          </div>
          <div className="flex items-center gap-1 text-[12px] text-[#8b95a5]">
            <LocationDot size={12} />
            <span className="truncate">{post.location}</span>
          </div>
        </div>
        <button onClick={() => setMenuOpen(true)} className="p-1 active:scale-90 transition-transform">
          <MoreHorizontal size={22} className="text-[#c4ccd8]" />
        </button>
      </div>

      <div className="relative" onDoubleClick={toggleLike}>
        {post.type === 'video' ? (
          <button onClick={() => setVideoOpen(true)} className="relative block w-full group">
            <img src={ytThumb(post.youtubeId)} alt="" className="w-full aspect-video object-cover" onError={(e) => { e.target.src = `https://img.youtube.com/vi/${post.youtubeId}/hqdefault.jpg`; }} />
            <span className="absolute inset-0 bg-black/25 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-[#f43f5e] flex items-center justify-center shadow-xl group-active:scale-90 transition-transform">
                <Play size={28} className="text-white fill-white ml-1" />
              </span>
            </span>
            <span className="absolute bottom-3 left-3 text-[11px] font-semibold text-white bg-black/60 px-2 py-1 rounded">YouTube</span>
          </button>
        ) : (
          <img src={post.image} alt="" className="w-full aspect-square object-cover" />
        )}
        {product && (
          <button onClick={() => setProductOpen(true)} className="absolute bottom-3 left-3 flex items-center gap-1.5 text-[12px] font-semibold text-white bg-black/65 backdrop-blur px-3 py-1.5 rounded-full active:scale-95 transition-transform">
            <ShoppingBag size={14} className="text-[#2f8bff]" /> Shop this
          </button>
        )}
      </div>

      {tagged.length > 0 && (
        <div className="flex items-center gap-1.5 px-4 pt-2.5 text-[12px] text-[#8b95a5]">
          <Tag size={13} className="text-[#2f8bff]" />
          <span>with {tagged.map((t) => `@${t.handle}`).join(', ')}</span>
        </div>
      )}

      <div className="px-4 pt-3">
        <div className="flex items-center gap-5">
          <button onClick={toggleLike}>
            <Heart size={24} className={`${liked ? 'fill-[#f43f5e] text-[#f43f5e]' : 'text-white'} ${pop ? 'animate-heart' : ''} transition-colors`} />
          </button>
          <button onClick={() => setCommentsOpen(true)} className="active:scale-90 transition-transform"><MessageCircle size={23} className="text-white" /></button>
          <button onClick={toggleSave} className="ml-auto active:scale-90 transition-transform">
            <Bookmark size={23} className={saved ? 'fill-[#2f8bff] text-[#2f8bff]' : 'text-white'} />
          </button>
        </div>
        <p className="text-sm font-semibold text-white mt-2.5">{likeCount.toLocaleString()} likes</p>
        <p className="text-sm text-[#dbe1ea] mt-1 leading-snug">
          <span className="font-semibold text-white">{post.handle} </span>
          <RichText text={post.caption} />
        </p>

        {product && (
          <button onClick={() => setProductOpen(true)} className="w-full mt-3 flex items-center gap-3 bg-[#0c1420] ring-1 ring-[#1c2634] rounded-xl p-2.5 active:scale-[0.99] transition-transform">
            <img src={product.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-white/5" />
            <div className="flex-1 text-left min-w-0">
              <p className="text-[13px] font-semibold text-white truncate">{product.name}</p>
              <p className="text-[12px] text-[#8b95a5]">₹{product.price.toLocaleString()} · {product.seller}</p>
            </div>
            <span className="text-[12px] font-semibold text-white bg-[#2f8bff] px-3 py-1.5 rounded-lg">View</span>
          </button>
        )}

        <button onClick={() => setCommentsOpen(true)} className="text-[13px] text-[#8b95a5] mt-2">View all {commentCount} comments</button>
      </div>
      <div className="h-px bg-[#141c28] mx-4 mt-4" />

      {videoOpen && <YouTubeModal videoId={post.youtubeId} onClose={() => setVideoOpen(false)} />}
      {productOpen && product && <ProductSheet product={product} onClose={() => setProductOpen(false)} />}
      <CommentsSheet open={commentsOpen} post={post} onClose={() => setCommentsOpen(false)} onCountChange={setCommentCount} />
      <ActionSheet open={menuOpen} name={post.user} onClose={() => setMenuOpen(false)} />
    </article>
  );
}

export default function HomeFeed() {
  useNow();
  return (
    <div>
      <StoriesRow />
      <DiscoverChips />
      <div className="h-px bg-[#141c28] mx-4" />
      <div className="pb-2">
        {homePosts.map((p) => <PostCard key={p.id} post={p} />)}
      </div>
      <p className="text-center text-[12px] text-[#5c6675] pb-6">You're all caught up</p>
    </div>
  );
}
